import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '..';
import type { Injections } from '../services/injections';
import type { ApplicationErrorOptions } from '../../types';
import type { ShipiumOauthClient, CreateOauthClientResponse, ShipiumApiKey } from '../services/SecurityService/types';
import parseStatusCode from '../errors/parseStatusCode';

export interface SecurityModel {
  securityActionStart: Action<SecurityModel, void>;
  getAuthorities: Thunk<SecurityModel, void, Injections, StoreModel>;
  setAuthorities: Action<SecurityModel, any[]>;
  authoritiesList: any[];
  lastAction?: number;
  // security
  oauthClientFailure: Action<SecurityModel, ApplicationErrorOptions>;
  createPartnerOauthClient: Thunk<
    SecurityModel,
    ShipiumOauthClient,
    Injections,
    StoreModel,
    Promise<CreateOauthClientResponse | undefined>
  >;
  resetPartnerOauthClient: Thunk<
    SecurityModel,
    ShipiumOauthClient,
    Injections,
    StoreModel,
    Promise<CreateOauthClientResponse | undefined>
  >;
  getPartnerOauthClients: Thunk<SecurityModel, string, Injections, StoreModel, Promise<any>>;
  setPartnerOauthClients: Action<SecurityModel, ShipiumOauthClient[]>;
  partnerOauthClients: ShipiumOauthClient[];
  setCreatedCredentials: Action<SecurityModel, Credentials>;
  createdCredentials?: Credentials;
  selectedOauthClient?: ShipiumOauthClient;
  setSelectedOauthClient: Action<SecurityModel, ShipiumOauthClient | undefined>;
  updatePartnerOauthClient: Thunk<
    SecurityModel,
    ShipiumOauthClient,
    Injections,
    StoreModel,
    Promise<ShipiumOauthClient | undefined>
  >;

  // API Keys
  getPartnerApiKeys: Thunk<SecurityModel, string, Injections, StoreModel, Promise<ShipiumApiKey[]>>;
  partnerApiKeys: ShipiumApiKey[];
  setPartnerApiKeys: Action<SecurityModel, ShipiumApiKey[]>;
  setSelectedApiKey: Action<SecurityModel, ShipiumApiKey>;
  updatePartnerApiKey: Thunk<
    SecurityModel,
    { partnerId: string; apiKey: ShipiumApiKey },
    Injections,
    StoreModel,
    Promise<ShipiumApiKey>
  >;
  createShipiumApiKey: Thunk<
    SecurityModel,
    { name: string; partnerId: string },
    Injections,
    StoreModel,
    Promise<ShipiumApiKey>
  >;
  selectedApiKey?: ShipiumApiKey;
}

export interface Credentials {
  apiKey?: string;
  apiKeyId?: string;
  oauthClientSecret?: string;
  oauthClientId?: string;
}

export const securityModel: SecurityModel = {
  partnerApiKeys: [],
  partnerOauthClients: [],
  authoritiesList: [],
  securityActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  getAuthorities: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { securityService } = injections;
      actions.securityActionStart();
      const res = await securityService.getAuthorities();
      actions.setAuthorities(res);
    } catch (error) {
      actions.oauthClientFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to fetch authorities',
      });
    }
  }),
  setAuthorities: action((state, payload) => {
    state.authoritiesList = payload;
  }),
  getPartnerOauthClients: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.securityActionStart();
      const { securityService } = injections;
      const res = await securityService.getPartnerOauthClients(payload);
      actions.setPartnerOauthClients(res);
    } catch (error) {
      actions.oauthClientFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to fetch clients',
        asNotification: true,
      });
    }
  }),
  oauthClientFailure: action((state, error) => {
    console.log(error);
  }),
  createPartnerOauthClient: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { securityService } = injections;
      const store = getStoreState();
      const res = await securityService.createPartnerOauthClient(
        store?.session?.user?.sessionPartnerId || '',
        payload.name,
      );
      actions.setPartnerOauthClients([...store.security.partnerOauthClients, res.oauthClient]);
      actions.setCreatedCredentials({
        oauthClientSecret: res.oauthClientSecret,
        oauthClientId: res.oauthClient.oauthClientId,
      });
      return res;
    } catch (error) {
      actions.oauthClientFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to create Oauth client',
        asNotification: true,
      });
    }
  }),
  resetPartnerOauthClient: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.securityActionStart();
      const { securityService } = injections;
      const store = getStoreState();
      const res = await securityService.resetPartnerOauthClient(store?.session?.user?.sessionPartnerId || '', payload);
      actions.setCreatedCredentials({
        oauthClientSecret: res.oauthClientSecret,
        oauthClientId: res.oauthClient.oauthClientId,
      });
      return res;
    } catch (error) {
      actions.oauthClientFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to reset Oauth client',
        asNotification: true,
      });
    }
  }),
  updatePartnerOauthClient: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const { securityService } = injections;
      const store = getStoreState();
      const res = await securityService.updatePartnerOauthClient(store?.session?.user?.sessionPartnerId || '', payload);
      const updatedOauthClients = store.security.partnerOauthClients.map((val) =>
        val.oauthClientId === res.oauthClientId ? res : val,
      );
      actions.setPartnerOauthClients(updatedOauthClients);
      return res;
    } catch (error) {
      actions.oauthClientFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to update Oauth Client',
        asNotification: true,
      });
    }
  }),
  setCreatedCredentials: action((state, payload) => {
    state.createdCredentials = payload;
  }),
  setPartnerOauthClients: action((state, payload) => {
    state.partnerOauthClients = payload;
  }),
  setSelectedOauthClient: action((state, payload) => {
    state.selectedOauthClient = payload;
  }),
  setSelectedApiKey: action((state, payload) => {
    state.selectedApiKey = payload;
  }),
  getPartnerApiKeys: thunk(async (actions, payload, { injections }) => {
    try {
      actions.securityActionStart();
      const { securityService } = injections;
      const res = await securityService.getApiKeys(payload);
      actions.setPartnerApiKeys(res);
      return res;
    } catch (error) {
      actions.oauthClientFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to Fetch API Keys',
        asNotification: true,
      });
      return error;
    }
  }),
  setPartnerApiKeys: action((state, payload) => {
    state.partnerApiKeys = payload;
  }),

  createShipiumApiKey: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.securityActionStart();
      const { securityService } = injections;
      const store = getStoreState();
      const res = await securityService.createPartnerApiKey(payload.partnerId, payload.name);
      actions.setCreatedCredentials({
        apiKey: res.apiKey,
        apiKeyId: res.apiKeyId,
      });
      actions.setPartnerApiKeys([...store.security.partnerApiKeys, res]);
      return res;
    } catch (error) {
      actions.oauthClientFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to create API key',
        asNotification: true,
      });
      return error;
    }
  }),
  updatePartnerApiKey: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.securityActionStart();
      const { securityService } = injections;
      const store = getStoreState();
      const res = await securityService.updatePartnerApiKey(payload.partnerId, payload.apiKey);
      actions.setPartnerApiKeys([
        ...store.security.partnerApiKeys.map((val) => {
          if (val.apiKeyId === payload.apiKey.apiKeyId) {
            return res;
          } else {
            return val;
          }
        }),
      ]);
      return res;
    } catch (error) {
      actions.oauthClientFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to update API Key',
        asNotification: true,
      });
      return error;
    }
  }),
};
